/**
 * Auth context: stores token + user, provides login/logout/refresh.
 * Axios interceptor in api.js reads token from localStorage on each request,
 * so we just keep React state in sync with localStorage here.
 */
import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [token, setTokenState] = useState(() => localStorage.getItem("mesafa_token"));
    const [ready, setReady] = useState(false);
    const bootRan = useRef(false);

    const persistToken = (t) => {
        if (t) localStorage.setItem("mesafa_token", t);
        else localStorage.removeItem("mesafa_token");
        setTokenState(t);
    };

    const refreshUser = useCallback(async () => {
        const t = localStorage.getItem("mesafa_token");
        if (!t) {
            setUser(null);
            setReady(true);
            return null;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            setReady(true);
            return data;
        } catch (e) {
            console.warn("[auth] refreshUser failed:", e?.response?.status);
            localStorage.removeItem("mesafa_token");
            setTokenState(null);
            setUser(null);
            setReady(true);
            return null;
        }
    }, []);

    // Initial boot: if token exists, fetch user.
    useEffect(() => {
        if (bootRan.current) return;
        bootRan.current = true;
        refreshUser();
    }, [refreshUser]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        persistToken(data.token);
        setUser(data.user);
        setReady(true);
        return data.user;
    };

    const register = async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        persistToken(data.token);
        setUser(data.user);
        setReady(true);
        return data.user;
    };

    // Entrar/registar via Google Identity Services (envia o ID token "credential").
    const googleAuth = async (credential) => {
        const { data } = await api.post("/auth/google", { credential });
        persistToken(data.token);
        setUser(data.user);
        setReady(true);
        return data.user;
    };

    const logout = () => {
        persistToken(null);
        setUser(null);
    };

    return (
        <AuthCtx.Provider value={{ user, token, ready, login, register, googleAuth, logout, refreshUser, setUser }}>
            {children}
        </AuthCtx.Provider>
    );
}

export const useAuth = () => useContext(AuthCtx);
